/**
 * Service-request type keys — the bits both packages need.
 *
 * A `service_requests` doc stores `requestType` as whatever key the tenant's
 * trade pack defines, so neither side can hold a closed list any more. Both
 * still have to render a label for a key they may never have seen: the client
 * in the requests list, the server in the Claude connector's summaries.
 *
 * `humanizeRequestTypeKey` is that shared last resort. It sits here rather
 * than being copied into both because a divergence would show up as the same
 * request reading "Test And Tag" in the app and "test_and_tag" in the
 * connector.
 *
 * Label RESOLUTION (pack → static list → humanised) stays per-package: the
 * client resolves through `useServiceRequestTypes()`, which needs React and a
 * live pack subscription; the server has neither.
 */

/**
 * The telco request types shipped before packs existed. Still the fallback
 * set for a pack that declares no `serviceRequestTypes` of its own.
 */
export const LEGACY_SERVICE_REQUEST_TYPE_LABELS: Record<string, string> = {
  nbn_upgrade: 'NBN Upgrade',
  speed_plan_change: 'Speed / Plan Change',
  voip_changes: 'VoIP Changes',
  account_billing_update: 'Account / Billing Update',
  config_remote_work: 'Configuration / Remote Work',
  relocating: 'Relocating',
  account_closure: 'Account Closure',
  other: 'Other',
};

/**
 * Turn a stored key into something readable when no pack and no legacy entry
 * claims it: `'test_and_tag'` → `'Test And Tag'`.
 *
 * Never returns an empty string for a non-empty key — the whole point is that
 * a row is never blank.
 */
export function humanizeRequestTypeKey(key: string): string {
  return key
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Best-effort label with no pack available: legacy list, else humanised key.
 * The server uses this directly; the client layers the active pack on top.
 */
export function fallbackRequestTypeLabel(key: string | undefined): string {
  if (!key) return '';
  return LEGACY_SERVICE_REQUEST_TYPE_LABELS[key] ?? humanizeRequestTypeKey(key);
}
